// @ts-nocheck
import { isValidEmail } from '../../../../../../utils/email/isValidEmail';
import { ChevronRight, Link2, Trash2, UserX } from 'lucide-react';
import {
  workflowTableHeadClass,
  workflowTableIconButtonDangerClass,
} from '../../../../components/WorkflowFormPrimitives';
import { pctFmt } from '../shared/voteRoomFormat';
import { ImportSummaryBanner } from './ImportSummaryBanner';
import { VoteRoomStatTile } from './VoteRoomStatTile';

/**
 * Second wizard step: stat strip + eligible shareholders table with DQI / §318 exclude actions, the
 * excluded-shareholders re-include strip, and step navigation. State and exclude/include handlers
 * are owned by the parent.
 */
export const ReviewShareholdersStep = ({
  locked,
  uploadedFileName,
  shareholders,
  eligibleShareholders,
  excludedShareholders,
  dqiExcludedCount,
  s318ExcludedCount,
  threshold75,
  totalShares,
  eligibleShares,
  hasEmails,
  dqis,
  onExcludeAsDqi,
  onOpen318Modal,
  onReinclude,
  onRequestRemoveRow,
  onBack,
  onContinue,
}) => {
  const reviewStats = [
    { label: 'Total Shareholders', value: shareholders.length },
    { label: 'Eligible to Vote', value: eligibleShareholders.length, color: 'text-emerald-600' },
    { label: 'Excluded (DQI)', value: dqiExcludedCount, color: 'text-amber-600' },
    { label: 'Excluded (Section 318)', value: s318ExcludedCount, color: 'text-amber-600' },
    { label: '75% Threshold', value: `${threshold75.toLocaleString()}`, color: 'text-emerald-600' },
  ];

  return (
    <div className="space-y-4">
      <ImportSummaryBanner
        uploadedFileName={uploadedFileName}
        shareholderCount={shareholders.length}
        dqiMatchCount={dqiExcludedCount}
      />

      <div className="grid grid-cols-5 gap-3">
        {reviewStats.map((s) => (
          <VoteRoomStatTile key={s.label} label={s.label} value={s.value} color={s.color} />
        ))}
      </div>

      <div className="bg-white border border-gray-200/80 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50/80 border-b border-gray-100">
            <tr>
              {['Name', 'Email', 'Shares', '% of Total', '% of Eligible', 'Exclude', ''].map((h) => (
                <th key={h} className={workflowTableHeadClass}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {eligibleShareholders.map((sh) => (
              <tr key={sh.id} className="hover:bg-gray-50/40">
                <td className="px-3 py-3 text-xs font-semibold text-gray-900">{sh.name}</td>
                <td className="px-3 py-3 text-xs text-gray-500">
                  {!sh.email ? (
                    <span className="text-red-400">Missing</span>
                  ) : isValidEmail(sh.email) ? (
                    sh.email
                  ) : (
                    <span className="text-red-400">Invalid</span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs text-gray-700 tabular-nums">{sh.shares.toLocaleString()}</td>
                <td className="px-3 py-3 text-xs text-gray-500 tabular-nums">
                  {totalShares > 0 ? pctFmt(sh.shares / totalShares) : '0.0%'}
                </td>
                <td className="px-3 py-3 text-xs text-gray-700 tabular-nums font-medium">
                  {eligibleShares > 0 ? pctFmt(sh.shares / eligibleShares) : '0.0%'}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => onExcludeAsDqi(sh.id)}
                      disabled={locked}
                      className="text-xs text-gray-500 hover:text-amber-600 font-medium transition-colors flex items-center space-x-1 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-500"
                    >
                      <UserX size={11} />
                      <span>DQI</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => dqis.length > 0 && onOpen318Modal(sh.id)}
                      disabled={locked || dqis.length === 0}
                      title={
                        locked
                          ? 'Vote Room launched — locked'
                          : dqis.length === 0
                            ? 'No DQIs over threshold — Section 318 attribution unavailable'
                            : 'Exclude as constructively owned by a DQI'
                      }
                      className="text-xs font-medium transition-colors flex items-center space-x-1 disabled:cursor-not-allowed disabled:opacity-40 text-gray-500 hover:text-amber-600 disabled:hover:text-gray-500"
                    >
                      <Link2 size={11} />
                      <span>Sec. 318</span>
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <button
                    type="button"
                    onClick={() => onRequestRemoveRow({ id: sh.id, name: sh.name || 'this shareholder' })}
                    disabled={locked}
                    className={`${workflowTableIconButtonDangerClass} disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={locked ? 'Vote Room launched — locked' : 'Remove row'}
                    aria-label={`Remove shareholder ${sh.name || 'row'}`}
                  >
                    <Trash2 size={18} strokeWidth={2} aria-hidden />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {excludedShareholders.length > 0 && (
        <div className="space-y-2">
          {excludedShareholders.map((sh) => (
            <div
              key={sh.id}
              className="px-4 py-2.5 rounded-md border border-amber-100 bg-amber-50/40 flex items-center justify-between"
            >
              <div className="text-xs text-amber-800">
                <span className="font-medium">{sh.name}</span> — {sh.shares.toLocaleString()} shares
              </div>
              <button
                type="button"
                onClick={() => onReinclude(sh.id)}
                disabled={locked}
                title={locked ? 'Vote Room launched — locked' : undefined}
                className="text-xs text-amber-700 hover:text-amber-900 font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-amber-700"
              >
                Re-include
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center px-3 py-2 rounded-md text-xs font-semibold bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          <ChevronRight size={13} className="mr-1 rotate-180" /> Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={eligibleShareholders.length === 0 || !hasEmails}
          className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold bg-[#0f172a] text-white hover:bg-[#1e293b] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          Continue to Shutdown Rule <ChevronRight size={13} className="ml-1.5" />
        </button>
      </div>
    </div>
  );
};
